import { useState, useEffect } from 'react';
import { Download, Share, PlusSquare, X } from 'lucide-react';
import usePWAInstall from '../hooks/usePWAInstall';
import { useLocation } from 'react-router-dom';

// The install button is only offered on these public pages.
const INSTALL_BUTTON_PATHS = ['/', '/login', '/register'];

// Fires analytics events only if the app already has an analytics setup
// (Step 15: don't add a new analytics platform just for this).
// Adjust `window.gtag` / `window.analytics` to whatever your app already uses,
// or remove this helper entirely if you don't have analytics yet.
function trackPwaEvent(eventName) {
  try {
    if (typeof window.gtag === 'function') {
      window.gtag('event', eventName);
    } else if (window.analytics && typeof window.analytics.track === 'function') {
      window.analytics.track(eventName);
    }
  } catch (e) {
    // Never let analytics failures break the install flow.
  }
}

/**
 * iOS/Safari doesn't support the native beforeinstallprompt flow (Step 9),
 * so this shows manual "Add to Home Screen" steps instead.
 */
function IosInstallInstructions({ onClose }) {
  return (
    <div
      className='fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/40 px-4'
      onClick={onClose}
    >
      <div
        className='bg-white rounded-2xl w-full max-w-sm p-6 shadow-xl'
        onClick={(e) => e.stopPropagation()}
      >
        <div className='flex items-center justify-between mb-4'>
          <h3 className='text-lg font-semibold text-[#0D1B3D]'>
            Install PlanB Jobs
          </h3>
          <button
            onClick={onClose}
            aria-label='Close'
            className='text-gray-400 hover:text-gray-600 p-1'
          >
            <X size={20} />
          </button>
        </div>

        <ol className='space-y-4'>
          <li className='flex items-start gap-3'>
            <span className='flex-shrink-0 w-7 h-7 rounded-full bg-[#EFF6FF] text-[#2563EB] font-semibold text-sm flex items-center justify-center'>
              1
            </span>
            <p className='text-sm text-gray-700 pt-1'>
              Tap the <Share size={14} className='inline mx-1 -mt-0.5' /> Share button in Safari's toolbar.
            </p>
          </li>
          <li className='flex items-start gap-3'>
            <span className='flex-shrink-0 w-7 h-7 rounded-full bg-[#EFF6FF] text-[#2563EB] font-semibold text-sm flex items-center justify-center'>
              2
            </span>
            <p className='text-sm text-gray-700 pt-1'>
              Select <PlusSquare size={14} className='inline mx-1 -mt-0.5' /> <strong>"Add to Home Screen"</strong>.
            </p>
          </li>
          <li className='flex items-start gap-3'>
            <span className='flex-shrink-0 w-7 h-7 rounded-full bg-[#EFF6FF] text-[#2563EB] font-semibold text-sm flex items-center justify-center'>
              3
            </span>
            <p className='text-sm text-gray-700 pt-1'>
              Tap <strong>"Add"</strong> in the top right corner.
            </p>
          </li>
        </ol>

        <button
          onClick={onClose}
          className='w-full mt-6 bg-[#F59E0B] text-white px-4 py-2 rounded-lg font-medium hover:opacity-90 transition'
        >
          Got it
        </button>
      </div>
    </div>
  );
}

/**
 * InstallAppButton
 *
 * Reusable across header, profile menu, dashboard, settings (Step 7).
 * - Hides itself automatically once installed (Step 10).
 * - Uses native install prompt on Android/Desktop Chrome/Edge (Step 8).
 * - Shows manual instructions modal on iOS (Step 9).
 *
 * Props:
 *  - variant: 'button' | 'menu-item' — controls styling only, same behavior.
 *  - className: optional extra classes to blend into different menus.
 */
export default function InstallAppButton({ variant = 'button', className = '' }) {
  const { canInstall, isInstalled, isIos, installApp } = usePWAInstall();
  const [showIosInstructions, setShowIosInstructions] = useState(false);
  const { pathname } = useLocation();
  const currentPath = pathname.replace(/\/+$/, '') || '/';
  const onAllowedPage = INSTALL_BUTTON_PATHS.includes(currentPath);

  const isVisible = onAllowedPage && !isInstalled && (canInstall || isIos);

  // Step 15: pwa_install_button_viewed — fires once per mount when the
  // button actually becomes visible to the user (not on every render).
  useEffect(() => {
    if (isVisible) {
      trackPwaEvent('pwa_install_button_viewed');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  // Only offer the install button on the home, login and register pages.
  if (!onAllowedPage) return null;

  // Step 10: never show the button (or iOS instructions) once installed.
  if (isInstalled) return null;

  // Neither the native prompt nor iOS Safari applies (e.g. unsupported
  // desktop browser) — nothing to show.
  if (!canInstall && !isIos) return null;

  const handleClick = async () => {
    trackPwaEvent('pwa_install_clicked');

    if (isIos) {
      setShowIosInstructions(true);
      return;
    }

    const result = await installApp();
    if (result.outcome === 'accepted') {
      trackPwaEvent('pwa_install_accepted');
    } else if (result.outcome === 'dismissed') {
      trackPwaEvent('pwa_install_dismissed');
    }
  };

  const baseStyles =
    variant === 'menu-item'
      ? 'flex items-center gap-3 w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition'
      : 'flex items-center gap-2 bg-[#2563EB] text-white px-4 py-2 rounded-lg font-medium hover:opacity-90 transition whitespace-nowrap';

  return (
    <>
      <button
        onClick={handleClick}
        className={`${baseStyles} ${className}`}
      >
        <Download size={variant === 'menu-item' ? 16 : 18} />
        Install App
      </button>

      {showIosInstructions && (
        <IosInstallInstructions onClose={() => setShowIosInstructions(false)} />
      )}
    </>
  );
}